import { createColumnHelper, FilterFn } from "@tanstack/react-table";
import dayjs from "dayjs";

import { BodyTypo } from "../../Common/BodyTypo";
import { HeaderTypo } from "../../Common/HeaderTypo";
import {
  ApprovedNotFundedColumnLabels,
  ApprovedNotFundedRow,
} from "../../types";
import { createBackofficeFieldDecorators } from "../../utils/redactedFieldsHelper";

const columnHelper = createColumnHelper<ApprovedNotFundedRow>();

export const rebateAccountFilter: FilterFn<ApprovedNotFundedRow> = (
  row,
  columnId,
  value,
) => {
  if (columnId !== "rebateAccount") return true;

  const rebateAccountID = row.original.rebateAccount;
  if (!value) return true;

  const selectedIDs = value as string[];

  return selectedIDs.includes(rebateAccountID);
};

export const clientApprovedNotFundedColumns = ({
  translatedContent,
  isBackoffice = false,
}: {
  translatedContent?: ApprovedNotFundedColumnLabels;
  language: string;
  redactedFields?: string[];
  isBackoffice?: boolean;
}) => {
  const { hiddenColumns, decorateTitle, getRedactedClassName } =
    createBackofficeFieldDecorators([], isBackoffice);
  const columns = [
    columnHelper.accessor("tradingAccountID", {
      header: () => <HeaderTypo text={translatedContent?.acc} />,
      cell: (info) => <BodyTypo text={`${info.getValue()}`} />,
      sortingFn: "alphanumeric",
      sortDescFirst: false,
      size: 120,
    }),
    columnHelper.accessor("tradingAccountType", {
      header: () => <HeaderTypo text={translatedContent?.accType} />,
      cell: (info) => <BodyTypo text={info.getValue()} />,
      enableGlobalFilter: false,
      sortingFn: "alphanumeric",
      size: 140,
    }),
    columnHelper.accessor("clientId", {
      header: () => (
        <HeaderTypo
          text={decorateTitle("clientId", translatedContent?.clientId)}
        />
      ),
      cell: (info) => <BodyTypo text={info.getValue()} />,
      sortingFn: "alphanumeric",
      sortDescFirst: false,
      enableGlobalFilter: true,
      size: 120,
      meta: { ...getRedactedClassName("clientId") },
    }),
    columnHelper.accessor("approvalDate", {
      header: () => <HeaderTypo text={translatedContent?.approvalDate} />,
      cell: (info) => {
        const { approvalDate } = info.row.original;
        return (
          <div className="flex flex-col">
            <BodyTypo
              text={
                approvalDate ? dayjs(approvalDate).format("DD MMM YYYY") : "-"
              }
            />
          </div>
        );
      },
      enableGlobalFilter: false,
      sortingFn: "alphanumeric",
      size: 180,
    }),
    columnHelper.accessor("rebateAccount", {
      header: () => <HeaderTypo text={translatedContent?.rebateAccount} />,
      cell: (info) => <BodyTypo text={`${info.getValue()}`} />,
      enableGlobalFilter: false,
      filterFn: rebateAccountFilter,
      enableColumnFilter: true,
      sortingFn: "alphanumeric",
      sortDescFirst: false,
      size: 174,
    }),
  ];

  return columns.filter((column) => {
    const columnKey = String(column.accessorKey).toLowerCase();
    return !hiddenColumns.map((k) => k.toLowerCase()).includes(columnKey);
  });
};
